
import React, { useEffect, useMemo, useState } from 'react';
import { LIST_STATE_KEYS } from '../../config/listStateKeys';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AdminUserAction, CompanyAction } from '../../actions/';
import { AlertAction } from '../../actions/AlertAction';
import { store } from '../../store';
import { RayForm, RayTable, RayTreeview } from '../../components/common';
import { AdminPosition } from '../../config/RayCodes';
import { Translate as t } from '../../config/Translate';
import { history } from '../../history';
import { isRootCompany } from '../../lib/licenseHelper';
import { collectDescendantCompanyIds } from '../../lib/issuanceScope';
import { isCompanySuperUser } from '../../lib/companyPermissionGuard';
import { AdminCompanyListHeader } from '../../components/common/TableHeader';
import { EditSaveCancelBar } from '../../components/common/Buttons';
import { buildCompanyFormControls, fetchBranchUserCombo } from '../../components/company/companyFormControls';
import { useBatchEdit } from '../../hooks/useBatchEdit';

const TABLE_OPTIONS = {
	search: true,
	keyCol: "companyId",
	itemperpage: 30,
};

const CompanyList = ({ user, companies, GetCompany, UpdateCompanyInfo }) => {

	const isU = user?.adminType == 'U';
	const myCompanyId = user?.companyInfo?.companyId;
	const companyList = useMemo(() => (Array.isArray(companies) ? companies : []), [companies]);

	const [wHg, setwHg] = useState(300);
	// 트리에서 고른 항목 ({ id, name, country }) — 선택 전에는 null(내 회사 기준)
	const [treeItem, setTreeItem] = useState(null);
	// 카드에 보여 줄 회사 ID — 트리·표 클릭으로 바뀐다. 'ALL'(id "0")·미선택은 회사 없음.
	const [cardCompanyId, setCardCompanyId] = useState(myCompanyId || "");
	// IMP-484: [수정]→[저장]·[취소] 로 한 번에 저장(useBatchEdit).
	const [userCombo, setUserCombo] = useState([]);
	const [distributors, setDistributor] = useState([]);

	// 목록이 비어 조회가 필요한 동안만 표에 로딩 표시(이미 적재돼 있으면 조회 안 함 → 로딩도 없음).
	const [listLoading, setListLoading] = useState(companyList.length === 0);

	useEffect(() => {
		// 조회 중 목록이 채워져 효과가 재실행되면 이전 효과의 finally 가 막혀 로딩이 남으므로 여기서 끈다.
		if (companyList.length !== 0) { setListLoading(false); return undefined; }
		let alive = true;
		setListLoading(true);
		Promise.resolve(GetCompany())
			.catch(() => { })
			.finally(() => { if (alive) setListLoading(false); });
		return () => { alive = false; };
	}, [companyList.length]);

	useEffect(() => {
		if (isU) return;
		let alive = true;
		fetchBranchUserCombo().then((list) => { if (alive) setUserCombo(list); });
		return () => { alive = false; };
	}, []);

	const cardCompany = useMemo(
		() => companyList.find(x => x?.companyId == cardCompanyId),
		[companyList, cardCompanyId]
	);

	const fieldNames = useMemo(() => buildCompanyFormControls({ company: {}, companyId: '', editable: false, authUser: user, userCombo: [] })
		.flatMap(c => c.items.map(item => item.name)), []);
	const toDraft = (c) => fieldNames.reduce((acc, n) => { acc[n] = c?.[n]; return acc; }, {});

	const { draft, editing, saving, errors, formKey, onChange, onEdit, onSave, onCancel } = useBatchEdit({
		source: cardCompany,
		toDraft,
		save: (changed) => UpdateCompanyInfo(cardCompany.companyId, changed),
		editable: !isU,
		// 선택 회사가 바뀌면 저장 중이어도 편집을 닫고 앞 회사 입력을 버린다(다른 회사 id 로 저장 방지).
		recordKey: cardCompanyId,
	});

	const viewCompany = cardCompany ? { ...cardCompany, ...draft } : cardCompany;

	useEffect(() => {
		let alive = true;
		if (!cardCompanyId || String(cardCompanyId) === "0") {
			setDistributor([]);
			return undefined;
		}
		(async () => {
			const companyUsers = await AdminUserAction.GetUserListByCompanyId(cardCompanyId);
			if (!alive) return;
			setDistributor((Array.isArray(companyUsers) ? companyUsers : []).map((u) => ({
				type: u?.position, name: u?.firstName + ' ' + u?.lastName, label: "test", val: u?.email
			})));
		})();
		return () => { alive = false; };
	}, [cardCompanyId]);

	const rows = useMemo(() => {
		const treeId = treeItem ? String(treeItem.id) : myCompanyId;
		const nameById = new Map(companyList.map(c => [c?.companyId, c?.name]));
		let scoped = companyList;
		if (treeId && treeId !== "0") {
			const ids = new Set([treeId, ...collectDescendantCompanyIds(companyList, treeId)]);
			scoped = companyList.filter(c => ids.has(c?.companyId));
		}
		return scoped.map(c => ({
			...c,
			parentName: (c?.parentCompanyId && nameById.get(c.parentCompanyId)) || "",
		}));
	}, [companyList, treeItem, myCompanyId]);

	const onChangeTreeviewItem = (item) => {
		if (item) {
			setTreeItem(item);
			setCardCompanyId(String(item.id ?? ""));
		}
	};

	const onSelectRow = (item) => {
		if (item?.companyId) setCardCompanyId(item.companyId);
	};

	// U-30(c): 카드에 실제 회사가 골라진 경우만 수정 화면 이동 가능 ('ALL'·미선택 제외)
	const canEditCompany = !!cardCompany?.companyId && String(cardCompany.companyId) !== "0";

	const companyEdit = (ce) => {
		if (!ce || String(ce) === "0") {
			store.dispatch(AlertAction.ErrorMsg(t('company.message.pleaseSelect')));
		}
		else {
			history.push("/Admin/AdminCompanyInfo/" + ce);
		}
	}

	const form_controls = buildCompanyFormControls({
		company: viewCompany,
		companyId: cardCompany?.companyId,
		editable: isU ? false : editing,
		authUser: user,
		userCombo,
	});

	const form_controls_hq =
		!isRootCompany(cardCompany) ? [{
			title: t('company.label.internalBranchContact'), items: [
				{ type: "input", name: "Email", label: t('common.field.email'), val: "Branch Contact" },
				{ type: "input", name: "Tel", label: t('common.field.tel'), val: cardCompany?.contactTel }
			]
		}
		] : [{
			title: t('company.label.hqContact'), items: [
				{ type: "input", name: "csEmail", label: t('service.label.cs'), val: "ray_cs@raymedical.co.kr" },
				{ type: "input", name: "serviceEmail", label: t('company.label.sales'), val: "ray_sales@raymedical.co.kr" }
			]
		}
		]

	const card = (
		<div className="card p-2">
			{!cardCompany && <div><i className='p-1'>{t('company.message.pleaseSelect')}</i></div>}
			{cardCompany &&
				<>
					<div className="d-flex align-items-center mb-1">
						<h3 className="mb-0">{cardCompany.name}</h3>
					</div>
					{!isU && editing && !isCompanySuperUser(user) && (
						<div className="alert alert-info py-2 small mb-1">
							🔒 {t('company.message.suOnlyFieldsReadOnly')}
						</div>
					)}
					<RayForm key={`${cardCompany.companyId}-${formKey}`}
						controls={form_controls}
						handleChangeData={onChange}
						errors={errors} />
					{!isU &&
						<div className="ps-1 pe-1 mb-1">
							<EditSaveCancelBar editing={editing} onEdit={onEdit} onSave={() => onSave(form_controls)} onCancel={onCancel} saving={saving} />
						</div>
					}
				</>
			}
			<div className="rayform section mt-1">
				<div className="section-title">{t('company.label.distributorContact')}</div>
				<div className="card">
					<div className="card-body pb-1 pt-1">
						<ul className="listview simple-listview no-space border-0">
							{distributors.length > 0 && (
								distributors.map((item, idx) => {
									return (
										<div className="form-group basic" key={idx}>
											<div className="input-wrapper">
												<label className="label">
													{AdminPosition.filter(x => x.code == item.type)[0] ? AdminPosition.filter(x => x.code == item.type)[0].title : t('staff')}
												</label>
												<div className="form-content multi">({item.name})  {item.val}</div>
											</div>
										</div>
									)
								})
							)}
							{distributors.length == 0 && (
								<div><i className='p-1'>{t('company.message.error.distributorContactNotExist')}</i></div>
							)}
						</ul>
					</div>
				</div>
			</div>
			<RayForm controls={form_controls_hq} nocard={false}></RayForm>
		</div>
	);

	return (
		<>
			<div className="extraHeader">
				<h2 className="mb-0" style={{ marginRight: '10px' }}>{t('company.label.companyList')}</h2>
				{
					companyList.length > 1 && !isU && treeItem?.name &&
					<span className="me-2">
						{treeItem.country && "[" + treeItem.country + "] "}{treeItem.name}
					</span>
				}

				{
					!isU &&
					<>
						<button className="btn btn-primary btn-sm mt-0 me-1" disabled={!canEditCompany} title={canEditCompany ? undefined : t('company.message.pleaseSelect')} onClick={() => companyEdit(cardCompany?.companyId)}>{t('company.button.editCompany')}</button>
						<div className="ray-right">
							<Link to="/Admin/AdminCompanyAdd" className="btn btn-primary btn-sm mt-0">
								<ion-icon name="add-outline"></ion-icon>
								{t('company.button.addCompany')}
							</Link>
							{canEditCompany && <Link to={`/Admin/AdminProductCompanyImport/${cardCompany.companyId}`} className="btn btn-secondary btn-sm mt-0 ms-1 me-1" >
								{t('company.button.importCompanyId')}
							</Link>}
						</div>
					</>
				}
			</div>
			<div id="appCapsule" className="extra-header-active full-height">
				{
					!isU &&
					<div className="section fl p-0" style={{ width: wHg }}>
						<div className='p-1'>
							<RayTreeview stateKey={LIST_STATE_KEYS.ADMIN_COMPANY} onClickItem={onChangeTreeviewItem} onResize={setwHg} />
						</div>
					</div>
				}
				<div className="section m-1 p-0">
					{isU ? card :
						<div className="row">
							<div className="col-12 col-xl-7">
								<div className="card p-2">
									<RayTable
										data={rows}
										columns={AdminCompanyListHeader}
										isLoading={listLoading}
										rowSelectCallback={onSelectRow}
										options={TABLE_OPTIONS}
									/>
								</div>
							</div>
							<div className="col-12 col-xl-5">
								{card}
							</div>
						</div>
					}
				</div>
			</div>
		</>
	);
};

const mapState = (state) => {
	const user = state.AuthReducer.user;
	const companies = state.CompanyReducer.companies;
	return { user, companies };
}

const mapDispatch = dispatch => ({
	GetCompany: () => dispatch(CompanyAction.GetCompany()),
	UpdateCompanyInfo: (companyid, data) => dispatch(CompanyAction.UpdateCompanyInfo(companyid, data)),
})

export default connect(mapState, mapDispatch)(CompanyList);
